/**
 * Query keys de ai — ARIA: misión, autonomía, parte diario, acciones autónomas y reglas aprendidas.
 *
 * Se componen en `src/lib/qk.ts`, que sigue siendo el único punto de import de
 * toda la app (`@/lib/qk`). Aquí solo vive la definición; añadir una clave nueva
 * de este dominio se hace en este fichero y aparece sola en `qk`.
 *
 * Las convenciones (entidad en singular primero, `companyId` después, subsegmentos
 * en medio) están explicadas en `src/lib/qk.ts` y siguen aplicando.
 */
export const aiKeys = {
  // Panel admin de conversaciones analizadas por IA (pages/AIConversationsDashboard.jsx).
  aiConversation: {
    list: (companyId?: string, sentiment?: string, days?: string | number) =>
      ['ai-conversations', companyId, sentiment, days] as const,
    detail: (companyId?: string, conversationId?: string) =>
      ['ai-conversation-detail', companyId, conversationId] as const,
  },

  adaptiveDashboard: {
    all: () => ['adaptive-dashboard'] as const,
    snapshot: (companyId: string, userId: string, permissionScope: string, period: string, horizonDays: number, timeZone: string | null) =>
      ['adaptive-dashboard', companyId, userId, permissionScope, period, horizonDays, timeZone, 'snapshot-v1'] as const,
    detail: (companyId: string, userId: string, permissionScope: string, snapshotId: string, widgetId: string) =>
      ['adaptive-dashboard', companyId, userId, permissionScope, snapshotId, widgetId, 'detail-v1'] as const,
  },
  // ── PR11 CierreJourney — misión de cierre (GET /api/aria/mission) ─────────
  aria: {
    mission: (companyId?: string, period?: string, year?: number, month?: number) =>
      ['aria-mission', companyId, period, year, month] as const,
  },

  // ── Reinvención Conta PR13 — reglas de imputación aprendidas por ARIA ──────
  // (Configuración → Reglas aprendidas). GET /api/ai/preferences/learned.
  // `prefix` = 'supplier_account' | 'category_account' | undefined (ambas).
  ariaLearnedRule: {
    all: () => ['aria-learned-rules'] as const,
    list: (companyId?: string, prefix?: string) =>
      ['aria-learned-rules', companyId, prefix ?? 'all'] as const,
  },

  // ── N-FE3 Nivel 3 — parte diario firmable de ARIA ─────────────────────────
  // GET /api/aria/daily-reports?limit= (lista + streak/unsigned_count/degraded),
  // GET /:id (detalle), POST /:id/sign. Prefijo propio 'aria-daily-reports' →
  // firmar invalida list+detail de un tiro (y aparte ['aria-pulse'] por to_sign).
  ariaDailyReport: {
    all: () => ['aria-daily-reports'] as const,
    list: (companyId?: string, limit?: number) =>
      ['aria-daily-reports', companyId, limit ?? null] as const,
    detail: (companyId?: string, id?: string) =>
      ['aria-daily-reports', 'detail', companyId, id] as const,
  },

  // ── N-FE4 Nivel 3 — acciones autónomas de ARIA (extorno one-click) ────────
  // GET /api/aria/autonomous-actions (feed torre de control) · GET /:id (detalle
  // + reversal_preview) · POST /:id/reverse. `all()` invalida feed y detalles de
  // un tiro tras extornar. Objetos planos (sin envelope), igual que el resto de
  // /api/aria/*.
  ariaAutonomousAction: {
    all: () => ['aria-autonomous-actions'] as const,
    feed: (companyId?: string, status?: string, limit?: number) =>
      ['aria-autonomous-actions', companyId, status ?? null, limit ?? null] as const,
    detail: (companyId?: string, id?: string) =>
      ['aria-autonomous-actions', 'detail', companyId, id] as const,
  },

  // ── WS-C Nivel 3 — consola de ratios de autonomía (torre de control) ──────
  // GET /api/aria/autonomy-metrics?window= (auto/propuesto/fallo/extorno + %).
  // Read-only; el endpoint aún no está desplegado → el panel degrada limpio si
  // 404/error. Objeto plano o `{data}` (unwrapOne defensivo), igual que /api/aria/*.
  ariaMetrics: {
    all: () => ['aria', 'autonomy-metrics'] as const,
    window: (companyId?: string, window?: number) =>
      ['aria', 'autonomy-metrics', companyId, window ?? null] as const,
  },

  // ── N-FE1 Nivel 3 — dial de autonomía de ARIA (Configuración) ─────────────
  // GET/PUT /api/aria/autonomy. Al mutar se invalida también ['aria-pulse']
  // (el dial mueve el estado degradado/to_sign que muestra el pulso).
  ariaAutonomy: {
    all: () => ['aria-autonomy-settings'] as const,
    settings: (companyId?: string) => ['aria-autonomy-settings', companyId] as const,
  },
} as const;

